import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from "recharts";
import {
  Search,
  MoreVertical,
  ArrowUpRight,
  Share2,
  Edit,
  Trash2,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import StockUpdateForm from "./StockUpdateforTask";
import MaterialEditDeleteSection from "./MaterialEditDelete";
import { useAlert } from "components/UI_components/Alert";

const InventoryItemDetails = ({
  item: initialItem,
  onBack,
  userPermissions,
}) => {
  const alert = useAlert();
  const [item, setItem] = useState(initialItem);
  const [stockData, setStockData] = useState([]);
  const [showStockForm, setShowStockForm] = useState(false);
  const [stockSummary, setStockSummary] = useState({
    positive: 0,
    negative: 0,
    inStock: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [refresh, setRefresh] = useState(false);

  const calculateStockSummary = (data) => {
    const summary = data.reduce(
      (acc, curr) => {
        if (curr.entered_quantity > 0) {
          acc.positive += curr.entered_quantity;
        } else {
          acc.negative += Math.abs(curr.entered_quantity);
        }
        return acc;
      },
      { positive: 0, negative: 0 }
    );

    summary.inStock = summary.positive - summary.negative;
    return summary;
  };

  useEffect(() => {
    const fetchStockData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/inventory/get-stocks-of-a-material/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken.trim()}`,
            },
            body: JSON.stringify({
              material: item.id,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch stock data");
        }

        const data = await response.json();
        setStockData(data.data);
        const summary = calculateStockSummary(data.data);
        setStockSummary(summary);
      } catch (error) {
        console.error("Error fetching stock data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    const fetchMaterial = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/inventory/get-a-material/${item.id}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken.trim()}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch material data");
        }
        const data = await response.json();
        setItem(data.data);
      } catch (error) {
        console.error("Error fetching material data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaterial();
    fetchStockData();
  }, [item.id, refresh]);

  const handleStockUpdate = (newStockData) => {
    // Add the new stock data to the beginning of the array
    const updatedStockData = [newStockData, ...stockData];
    setStockData(updatedStockData);

    // Recalculate the summary
    const newSummary = calculateStockSummary(updatedStockData);
    setStockSummary(newSummary);
  };

  const handleEditStock = (stock) => {
    if (
      !userPermissions?.edit_material_management &&
      !userPermissions?.edit_all &&
      !userPermissions?.is_admin
    ) {
      alert.addAlert("You don't have permission to edit stock", "error");
      return;
    }
    setEditingStock(stock);
    setShowStockForm(true);
  };

  const handleDeleteStock = async (stockId) => {
    if (
      !userPermissions?.edit_material_management &&
      !userPermissions?.edit_all &&
      !userPermissions?.is_admin
    ) {
      alert.addAlert("You don't have permission to delete stock", "error");
      return;
    }

    if (window.confirm("Are you sure you want to delete this stock update?")) {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/inventory/delete-stock/${stockId}/`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${accessToken.trim()}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete stock");
        }

        // Remove the deleted stock from the state
        const updatedStockData = stockData.filter(
          (stock) => stock.id !== stockId
        );
        setStockData(updatedStockData);

        // Recalculate the summary
        const newSummary = calculateStockSummary(updatedStockData);
        setStockSummary(newSummary);

        alert.addAlert("Stock deleted successfully", "success");
      } catch (error) {
        console.error("Error deleting stock:", error);
        alert.addAlert(`Error deleting stock: ${error.message}`, "error");
      }
    }
  };

  const handleUpdateStockSubmit = async (updatedStockData) => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/update-stock/${editingStock.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken.trim()}`,
          },
          body: JSON.stringify({
            updatedStockData,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update stock");
      }

      // Update the stock data in the state
      const updatedStockList = stockData.map((stock) =>
        stock.id === editingStock.id ? { ...stock, ...updatedStockData } : stock
      );

      setStockData(updatedStockList);

      // Recalculate the summary
      const newSummary = calculateStockSummary(updatedStockList);
      setStockSummary(newSummary);

      setShowStockForm(false);
      setEditingStock(null);
      alert.addAlert("Stock updated successfully", "success");
    } catch (error) {
      console.error("Error updating stock:", error);
      alert.addAlert(`Error updating stock: ${error.message}`, "error");
    }
  };

  // Handle material update
  const handleMaterialUpdated = (updatedMaterial) => {
    setItem({ ...item, ...updatedMaterial });
    setRefresh(!refresh);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {

      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };


  // Prepare continuous *day-wise* stock data for the chart.
  // 1. Group all updates on the same date into a single net change.
  // 2. Build a cumulative stock level over those days.
  // 3. Clamp at 0 so it never goes negative.
  // 4. Draw segments between consecutive days, coloring them:
  //    - green when the stock level increases
  //    - red when the stock level decreases
  const sortedStockData = [...stockData].sort(
    (a, b) => new Date(a.update_date) - new Date(b.update_date)
  );

  // Aggregate by calendar day (YYYY-MM-DD) so multiple updates in the
  // same day become a single net change for that day. This gives a
  // smoother "sinusoidal" looking curve.
  const getDayKey = (dateLike) => {
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const dailyMap = new Map();
  sortedStockData.forEach((stock) => {
    const dayKey = getDayKey(stock.update_date);
    if (!dayKey) return; // skip invalid dates to avoid runtime crash
    const existing = dailyMap.get(dayKey) || {
      date: stock.update_date,
      netChange: 0,
    };
    existing.netChange += stock.entered_quantity;
    dailyMap.set(dayKey, existing);
  });

  const dailyChanges = Array.from(dailyMap.values());

  let runningStock = 0;
  const cumulativeChartPoints = dailyChanges.map((day, index) => {
    // Apply net change for that day and clamp at 0 (no negative stock)
    runningStock += day.netChange;
    if (runningStock < 0) runningStock = 0;

    return {
      index: index + 1, // Day 1, Day 2, ...
      date: day.date,
      stockLevel: runningStock,
      change: day.netChange,
    };
  });

  // This is used by the X and Y axes (domain, ticks, etc.)
  const chartData = cumulativeChartPoints;
  const xTicks = chartData.map((p) => p.index);
  const xDomainMax = xTicks.length ? xTicks[xTicks.length - 1] : 1;

  // Build small segments between each consecutive point with color
  // determined by whether stock increased or decreased.
  const stockSegments = [];
  for (let i = 0; i < cumulativeChartPoints.length - 1; i++) {
    const current = cumulativeChartPoints[i];
    const next = cumulativeChartPoints[i + 1];
    const isGain = next.stockLevel >= current.stockLevel;

    stockSegments.push({
      id: `${current.index}-${next.index}`,
      color: isGain ? "#10B981" : "#EF4444", // green for gain, red for fall
      data: [
        { index: current.index, stockLevel: current.stockLevel },
        { index: next.index, stockLevel: next.stockLevel },
      ],
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-600 mb-4">Error: {error}</div>
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800">
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen w-full">
      <button
        onClick={onBack}
        className="mb-4 text-blue-600 hover:text-blue-800"
      >
        ← Back
      </button>

      {/* Main Container with Material Details, Stock Details, and Graph */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Section: Material Details and Stock Summary */}
        <div className="bg-white rounded-lg shadow p-4">
          {/* Material Details */}
          <div className="flex justify-between items-start border-b border-gray-200 pb-4 mb-4">
            <div>
              <h1 className="text-xl font-semibold mb-1">
                {item.material_name || "Material Name"}
              </h1>
              <div className="text-sm text-gray-600">
                Sub Category:{" "}
                <span className="text-gray-900">
                  {item.subcategory || "N/A"}
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-600 text-right">
              Specification:{" "}
              <span className="text-black">{item.specification || "N/A"}</span>
            </div>
          </div>

          {/* Material Edit/Delete Buttons */}
          <MaterialEditDeleteSection
            item={item}
            onBack={onBack}
            userPermissions={userPermissions}
            onMaterialUpdated={handleMaterialUpdated}
          />

          {/* Stock Details Section */}
          <h3 className="text-sm font-medium mb-4 mt-6">Stock Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50">
              <div className="w-4 h-4 bg-purple-100 rounded-lg flex items-center justify-center">
                <Share2 className="w-3 h-3 text-purple-600" />
              </div>
              <span className="text-gray-600 text-sm">InStock:</span>
              <span className="font-medium text-sm">
                {stockSummary.inStock.toFixed(2)} {item?.uom}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50">
              <div className="w-4 h-4 bg-green-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-3 h-3 text-green-600" />
              </div>
              <span className="text-gray-600 text-sm">Added:</span>
              <span className="font-medium text-sm">
                {stockSummary.positive.toFixed(2)} {item?.uom}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50">
              <div className="w-4 h-4 bg-red-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-3 h-3 text-red-600 rotate-90" />
              </div>
              <span className="text-gray-600 text-sm">Used:</span>
              <span className="font-medium text-sm">
                {stockSummary.negative.toFixed(2)} {item?.uom}
              </span>
            </div>
          </div>
        </div>

        {/* Right Section: Stock Updates Graph */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <button className="text-gray-600 border-b-2 border-gray-900 pb-1">
              Stock Updates
            </button>
          </div>
          <h3 className="text-sm font-medium mb-4">Change in stock</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <XAxis
                type="number"
                dataKey="index"
                domain={[1, xDomainMax]}
                ticks={xTicks}
                allowDataOverflow
                tickFormatter={(value) => `Day ${value}`}
              />
              <YAxis />
              {/* Invisible base line so axes know the full domain */}
              <Line
                type="monotone"
                dataKey="stockLevel"
                stroke="transparent"
                dot={false}
                isAnimationActive={false}
              />
              {/* Colored continuous segments between days */}
              {stockSegments.map((segment) => (
                <Line
                  key={segment.id}
                  type="monotone"
                  data={segment.data}
                  dataKey="stockLevel"
                  stroke={segment.color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  legendType="none"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stock Update History Section */}
      <div className="bg-white rounded-lg shadow p-4 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Stock Update History</h3>
          <div className="flex items-center ml-auto mr-10">
            <button
              className="bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 pr-2 hover:bg-purple-800 transition-colors"
              onClick={() => {
                if (
                  !userPermissions?.edit_material_management &&
                  !userPermissions?.edit_all &&
                  !userPermissions?.is_admin
                ) {
                  alert.addAlert(
                    "You don't have permission to update stock",
                    "error"
                  );
                  return;
                }
                setEditingStock(null);
                setShowStockForm(true);
              }}
            >
              <ArrowUpRight className="w-4 h-4" />
              Update Stock
            </button>
          </div>
        </div>
        <div className="space-y-4">
          {stockData.map((item) => (
            <div
              key={item.id}
              className="grid grid-cols-[auto,1fr,auto] gap-4 items-center border-b pb-3 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-6 h-6 flex items-center justify-center rounded-lg ${
                    item.entered_quantity > 0 ? "bg-green-100" : "bg-red-100"
                  }`}
                >
                  <ArrowUpRight
                    className={`w-4 h-4 ${
                      item.entered_quantity > 0
                        ? "text-green-600"
                        : "text-red-600 rotate-90"
                    }`}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${
                        item.entered_quantity > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {item.entered_quantity > 0 ? "+" : ""}
                      {Math.abs(item.entered_quantity)} {item?.uom}
                    </span>
                    <span className="text-sm text-gray-500">
                      {item.entered_quantity > 0 ? "Added" : "Used"}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDate(item.update_date) + " " + formatDateTime(item?.last_updated_on)}
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {item.comment
                  ? `Comment: ${item.comment}`
                  : "No comment provided"}
              </div>
              <div className="flex items-center gap-3">
                {item.created_by && (
                  <div className="text-sm text-gray-500 mr-2">
                    Updated by: {item.created_by}
                  </div>
                )}
                <button
                  className="p-1 hover:bg-gray-100 rounded text-blue-600"
                  onClick={() => handleEditStock(item)}
                  title="Edit stock"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  className="p-1 hover:bg-gray-100 rounded text-red-600"
                  onClick={() => handleDeleteStock(item.id)}
                  title="Delete stock"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stock Update Modal */}
      {showStockForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <StockUpdateForm
            onClose={() => {
              setShowStockForm(false);
              setEditingStock(null);
              setRefresh(!refresh);
            }}
            materialId={item.id}
            currentStock={stockSummary.inStock}
            onStockUpdate={
              editingStock ? handleUpdateStockSubmit : handleStockUpdate
            }
            materialDetails={item}
            editMode={!!editingStock}
            stockData={editingStock || {}}
          />
        </div>
      )}
    </div>
  );
};

export default InventoryItemDetails;
